<template>
  <div>
    <div class="page-head">
      <h1>Subject Catalog</h1>
      <p>Four quarterly windows, each subject offered once per year in its assigned window. <b>Registration is open for the current quarter only</b> — upcoming windows show when their registration opens.</p>
    </div>
    <div v-if="currentQuarterLabel" class="notice">
      <b>Registration is open:</b> {{ noticeText }}
    </div>
    <div class="filters">
      <button
        v-for="cat in categories"
        :key="cat"
        type="button"
        class="fbtn"
        :class="{ active: activeCategory === cat }"
        @click="activeCategory = cat"
      >{{ cat }}</button>
    </div>
    <dsa-loading v-if="loading" text="Loading catalog…" />
    <div v-else class="wgrid">
      <div
        v-for="block in quarterBlocks"
        :key="block.quarter_code"
        class="wcard"
        :class="block.isCurrent ? 'current' : 'upcoming'"
      >
        <div class="wcard-head">
          <h3>{{ block.label }}</h3>
          <span v-if="block.isCurrent" class="chip open">Current Window</span>
        </div>
        <div class="wcard-dates"><b>{{ block.examHint }}</b><br>{{ block.regHint }}</div>
        <div v-if="filteredSubjects(block).length === 0" class="empty" style="padding:14px 0;border:none">No subjects in this category this window</div>
        <div v-for="sub in filteredSubjects(block)" :key="sub.id" class="subject-row">
          <div>
            <span class="sname">{{ sub.project_product || sub.dsa_subject_name || sub.project_ename }}</span>
            <span class="scat">{{ subjectCategory(sub) }}</span>
            <span v-if="sub.attempt_count > 0" class="scat">{{ sub.attempt_count }} / {{ sub.max_attempts || 3 }} attempts used</span>
          </div>
          <div class="sact">
            <span v-if="sub.registered" class="chip open">Enrolled</span>
            <span v-else-if="block.isCurrent && sub.can_register === false" class="chip pend">Max attempts</span>
            <button
              v-else-if="block.registration_open"
              type="button"
              class="btn btn-primary btn-sm"
              @click="openRegister(sub)"
            >Register</button>
          </div>
        </div>
      </div>
    </div>
    <div class="rule-card"><b>How it works:</b> each subject is assigned to exactly one quarterly window per year and is offered once annually. Registration opens only for the current quarter and closes about one week before testing. Max 3 attempts per subject.</div>

    <div v-if="registerModal" class="modal-overlay" @click.self="registerModal = false">
      <div class="modal-card">
        <h3>Register for {{ selectedSubject && (selectedSubject.project_product || selectedSubject.dsa_subject_name) }}</h3>
        <p v-if="selectedSubject && selectedSubject.attempt_count != null" class="hint">
          Attempts used for this subject: {{ selectedSubject.attempt_count }} / {{ selectedSubject.max_attempts || 3 }}
        </p>
        <p>Select a Pass to complete registration:</p>
        <div v-if="passes.length === 0" class="empty pass-empty" style="margin:16px 0">
          No valid Pass available. Purchase a DSA Pass first.
        </div>
        <label v-for="p in passes" :key="p.pass_id" class="pass-option">
          <input v-model="selectedPassId" type="radio" :value="p.pass_id">
          {{ p.pass_name || 'DSA Pass' }} — {{ p.remaining }} remaining
        </label>
        <div class="actions" style="margin-top:20px">
          <button type="button" class="btn btn-outline" @click="registerModal = false">Cancel</button>
          <button type="button" class="btn btn-primary" :disabled="registering" @click="confirmRegister">Confirm</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getStudentCatalogBatch, getPassWallet, registerStudentWithPass } from '@/api/dsa'
import { getUserId } from '@/utils/store'
import { subjectCategory, SUBJECT_CATEGORIES, unwrapId, buildCatalogQuarters, formatExamHint, formatRegistrationHint } from '@/utils/dsaFormat'

export default {
  name: 'StudentCatalog',
  data() {
    return {
      loading: true,
      quarters: [],
      quarterBlocks: [],
      activeCategory: SUBJECT_CATEGORIES[0],
      categories: SUBJECT_CATEGORIES,
      registerModal: false,
      selectedSubject: null,
      selectedPassId: '',
      passes: [],
      registering: false,
    }
  },
  computed: {
    currentBlock() {
      return this.quarterBlocks.find(b => b.isCurrent) || null
    },
    currentQuarterLabel() {
      return this.currentBlock ? this.currentBlock.label : ''
    },
    noticeText() {
      if (!this.currentBlock) return ''
      return `${this.currentBlock.label} · ${this.currentBlock.regHint}`
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    subjectCategory,
    filteredSubjects(block) {
      return block.subjects.filter(s => subjectCategory(s) === this.activeCategory)
    },
    mapQuarterBlock(q, subjects) {
      const list = Array.isArray(subjects) ? subjects : []
      list.forEach(s => { s.id = unwrapId(s) || s.project_code })
      const isCurrent = !!q.isCurrent
      return {
        quarter_code: q.quarter_code,
        label: q.quarter_name || q.quarter_code,
        isCurrent,
        registration_open: !!q.registration_open,
        examHint: formatExamHint(q),
        regHint: formatRegistrationHint(q, isCurrent),
        subjects: list,
      }
    },
    async load() {
      const student_id = getUserId()
      try {
        // One round-trip: batch resolves current+next3 quarters when codes omitted.
        const batchRes = await getStudentCatalogBatch(student_id)
        const batchData = (batchRes.data && batchRes.data.data) || {}
        const quarters = buildCatalogQuarters(batchData.quarter_defs || [])
        this.quarters = quarters
        if (!quarters.length) {
          this.quarterBlocks = []
          return
        }
        const byCode = {}
        ;(batchData.quarters || []).forEach((row) => {
          if (row && row.quarter_code) {
            byCode[row.quarter_code] = row.subjects || []
          }
        })
        this.quarterBlocks = quarters.map(q => this.mapQuarterBlock(q, byCode[q.quarter_code] || []))
      } catch (e) {
        this.$emit('toast', 'Failed to load catalog')
      } finally {
        this.loading = false
      }
    },
    async openRegister(subject) {
      if (subject.can_register === false) {
        this.$emit('toast', 'Maximum of 3 attempts per subject reached')
        return
      }
      this.selectedSubject = subject
      this.selectedPassId = ''
      const res = await getPassWallet(getUserId())
      const wallet = (res.data && res.data.data) || {}
      this.passes = (wallet.passes || []).filter(p => (p.remaining || 0) > 0)
      this.registerModal = true
      if (this.passes.length === 1) {
        this.selectedPassId = this.passes[0].pass_id
      }
    },
    async confirmRegister() {
      if (!this.selectedSubject) return
      if (!this.selectedPassId) {
        this.registerModal = false
        this.$router.push('/student/pass')
        return
      }
      this.registering = true
      try {
        const project_id = unwrapId(this.selectedSubject) || this.selectedSubject.id
        const res = await registerStudentWithPass(getUserId(), project_id, this.selectedPassId)
        if (res.data && res.data.code === 0) {
          this.$emit('toast', 'Registration successful')
          this.registerModal = false
          // Local update — avoid full catalog reload.
          const subject = this.selectedSubject
          subject.registered = true
          subject.can_register = false
          subject.attempt_count = (subject.attempt_count || 0) + 1
          subject.attempts_remaining = Math.max(
            0,
            (subject.max_attempts || 3) - subject.attempt_count
          )
        } else {
          this.$emit('toast', (res.data && res.data.msg) || 'Registration failed')
        }
      } catch (e) {
        this.$emit('toast', 'Registration failed')
      } finally {
        this.registering = false
      }
    },
  },
}
</script>

<style scoped>
.sname{font-weight:500}
.hint{font-size:12.5px;color:var(--muted);margin-bottom:10px}
.modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;z-index:1000}
.modal-card{background:#fff;border-radius:14px;padding:24px;max-width:440px;width:90%}
.pass-option{display:block;margin:10px 0;font-size:14px}
.pass-empty{font-size:13.5px}
</style>
